import { useEffect, useState, type FormEvent } from 'react';
import type { CodigoStaff } from '@eventos-cien/shared';
import { Button } from '../components/Button';
import { Callout } from '../components/Callout';
import { Card } from '../components/Card';
import { TextField } from '../components/TextField';
import { apiAuth, ApiError } from '../lib/apiAuth';
import { useAdminEventoData } from './AdminEventoDataProvider';

interface CodigoConId extends CodigoStaff {
  codigoId: string;
}

export function CodigosStaffScreen() {
  const { eventoId } = useAdminEventoData();
  const [codigos, setCodigos] = useState<CodigoConId[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [etiqueta, setEtiqueta] = useState('');
  const [generando, setGenerando] = useState(false);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);

  async function cargar() {
    try {
      setCodigos(await apiAuth<CodigoConId[]>(`/admin/eventos/${eventoId}/codigos-staff`));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudieron cargar los códigos');
    }
  }

  useEffect(() => {
    void cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventoId]);

  async function generar(e: FormEvent) {
    e.preventDefault();
    if (!etiqueta.trim()) return;
    setError(null);
    setGenerando(true);
    try {
      await apiAuth(`/admin/eventos/${eventoId}/codigos-staff`, {
        method: 'POST',
        body: JSON.stringify({ etiqueta: etiqueta.trim() }),
      });
      setEtiqueta('');
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo generar el código');
    } finally {
      setGenerando(false);
    }
  }

  async function toggleActivo(c: CodigoConId) {
    setOcupado(c.codigoId);
    setError(null);
    try {
      await apiAuth(`/admin/eventos/${eventoId}/codigos-staff/${c.codigoId}/activo`, {
        method: 'PATCH',
        body: JSON.stringify({ activo: !c.activo }),
      });
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo actualizar');
    } finally {
      setOcupado(null);
    }
  }

  async function eliminar(c: CodigoConId) {
    setOcupado(c.codigoId);
    setError(null);
    try {
      await apiAuth(`/admin/eventos/${eventoId}/codigos-staff/${c.codigoId}`, { method: 'DELETE' });
      await cargar();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo eliminar');
    } finally {
      setOcupado(null);
    }
  }

  async function copiar(codigo: string) {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(codigo);
      setTimeout(() => setCopiado(null), 1600);
    } catch {
      /* sin portapapeles */
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      <Card className="p-5">
        <form className="flex flex-col gap-4" onSubmit={generar}>
          <div>
            <h2 className="text-sm font-semibold text-texto">Generar código de acceso</h2>
            <p className="mt-1 text-xs text-texto/50">
              El staff de puerta entra en /login/staff con este código, sin necesidad de cuenta ni
              contraseña. Desactivarlo revoca el acceso al instante.
            </p>
          </div>
          <TextField
            label="Etiqueta"
            placeholder="p.ej. Puerta 1, Ana"
            value={etiqueta}
            onChange={(e) => setEtiqueta(e.target.value)}
          />
          <Button type="submit" cargando={generando} disabled={!etiqueta.trim()}>
            Generar código
          </Button>
        </form>
      </Card>

      {error && <Callout tono="error">{error}</Callout>}

      <ul className="space-y-2">
        {(codigos ?? []).map((c) => (
          <li key={c.codigoId}>
            <Card className="space-y-3 p-4">
              <div className="flex items-center justify-between gap-3">
                <span className="truncate text-sm font-medium text-texto">{c.etiqueta}</span>
                <span
                  className={[
                    'shrink-0 rounded-full px-2.5 py-0.5 text-xs font-medium',
                    c.activo ? 'bg-emerald-50 text-emerald-700' : 'bg-texto/5 text-texto/50',
                  ].join(' ')}
                >
                  {c.activo ? 'activo' : 'inactivo'}
                </span>
              </div>

              <button
                type="button"
                onClick={() => copiar(c.codigo)}
                className="w-full rounded-lg border border-borde bg-navy/5 px-3.5 py-2.5 text-center font-mono text-base tracking-[0.3em] text-navy"
              >
                {copiado === c.codigo ? 'Copiado' : c.codigo}
              </button>

              <p className="text-xs text-texto/50">
                Último uso: {c.ultimoUso ? new Date(c.ultimoUso).toLocaleString() : 'nunca'}
              </p>

              <div className="flex gap-2">
                <Button
                  variante="secundario"
                  cargando={ocupado === c.codigoId}
                  onClick={() => toggleActivo(c)}
                >
                  {c.activo ? 'Desactivar' : 'Activar'}
                </Button>
                <Button variante="fantasma" disabled={ocupado === c.codigoId} onClick={() => eliminar(c)}>
                  Eliminar
                </Button>
              </div>
            </Card>
          </li>
        ))}
        {codigos && codigos.length === 0 && (
          <p className="text-sm text-texto/50">Todavía no hay códigos generados.</p>
        )}
      </ul>
    </div>
  );
}
